import { SipClientAnalyticsEvents } from '~/types/Analytics/SipClientAnalyticsEvents'
import { round } from '~/utils/math'

import { Analytics, Logger } from '@mangovoice/types'

import { estimateMos } from './estimateMos'
import { parseRtcStats } from './parseRtcStats'
import {
  CallDirection,
  CallQualitySample,
  CallQualitySummary,
  RtcStatsSnapshot,
} from './types'

/** How often to poll RTCPeerConnection.getStats() during an active call. */
const DEFAULT_INTERVAL_MS = 10_000

/** A sample at or below this MOS, or at/above this loss %, counts as degraded. */
const DEGRADED_MOS = 3.0
const DEGRADED_LOSS_PCT = 5

/**
 * Minimum packets (received + lost) in an interval before its loss ratio is
 * trustworthy. Below this, DTX/silence/hold can leave near-zero received where
 * a single stray lost packet would read as ~100% loss.
 */
const MIN_INTERVAL_PACKETS = 50

/**
 * Stop the monitor after this many consecutive getStats() failures — covers
 * the case where the connection dies without a clean SessionState.Terminated
 * (e.g. transport drop), so the poll interval doesn't leak.
 */
const MAX_CONSECUTIVE_FAILURES = 3

/**
 * Treat a getStats() call that doesn't settle within this window as a failure.
 * getStats() reads local state and normally resolves in well under a second; a
 * hang (seen on some browsers during teardown) would otherwise wedge the
 * in-flight guard forever, so we time it out and count it toward the self-stop.
 * Kept below the poll interval so a timeout resolves before the next tick.
 */
const DEFAULT_GETSTATS_TIMEOUT_MS = 5_000

/**
 * PostHog event names. SipClient's analytics instance is the `sipc` namespace,
 * so these surface as `sipc_call_quality_summary` / `sipc_call_quality_degraded`.
 */
const EVENT_SUMMARY = SipClientAnalyticsEvents.CallQualitySummary
const EVENT_DEGRADED = SipClientAnalyticsEvents.CallQualityDegraded

interface CallQualityMonitorOptions {
  callUuid: string
  direction: CallDirection
  /**
   * Lazily resolves the live RTCPeerConnection. Throws if unavailable — the
   * monitor swallows that and skips the sample, since the connection can vanish
   * mid-call (hold, transfer, teardown).
   */
  getPeerConnection: () => RTCPeerConnection
  analytics?: Analytics
  logger?: Logger
  intervalMs?: number
  getStatsTimeoutMs?: number
  /**
   * Invoked exactly once when the monitor stops — including a self-stop (peer
   * connection closed/failed or repeated getStats failures) that the owner never
   * explicitly requested. Lets the owner drop its reference so a monitor that
   * dies without a clean SessionState.Terminated doesn't leak.
   */
  onStop?: () => void
}

/**
 * Polls WebRTC stats for a single call, derives per-interval quality samples,
 * and reports a low-volume summary to analytics on teardown. Emits a one-shot
 * `call_quality_degraded` event the first time quality crosses a bad threshold
 * so mid-call problems are visible even when the average looks fine.
 *
 * All collected data is numeric + direction + the internal call UUID — no PHI.
 */
export class CallQualityMonitor {
  private readonly callUuid: string
  private readonly direction: CallDirection
  private readonly getPeerConnection: () => RTCPeerConnection
  private readonly analytics?: Analytics
  private readonly logger?: Logger
  private readonly intervalMs: number
  private readonly getStatsTimeoutMs: number
  private readonly onStop?: () => void

  private intervalId: ReturnType<typeof setInterval> | null = null
  private stopped = false
  private startedAtMs = 0
  private consecutiveFailures = 0
  private prevSnapshot: RtcStatsSnapshot | null = null
  private readonly samples: CallQualitySample[] = []
  /** Guards against overlapping polls if a getStats() call outlives the interval. */
  private polling = false
  /** Sticky: set on the first bad sample, also gates the one-shot degraded event. */
  private degraded = false

  /** True while actively sampling; false once stopped (or never started). */
  get isRunning(): boolean {
    return !this.stopped && this.intervalId !== null
  }

  constructor(options: CallQualityMonitorOptions) {
    this.callUuid = options.callUuid
    this.direction = options.direction
    this.getPeerConnection = options.getPeerConnection
    this.analytics = options.analytics
    this.logger = options.logger
    this.intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS
    this.getStatsTimeoutMs =
      options.getStatsTimeoutMs ?? DEFAULT_GETSTATS_TIMEOUT_MS
    this.onStop = options.onStop
  }

  start(): void {
    if (this.intervalId !== null || this.stopped) return
    this.startedAtMs = Date.now()
    this.intervalId = setInterval(() => {
      // poll() guards itself, but catch here too so a stray rejection from the
      // timer callback can never become an unhandled promise rejection.
      this.poll().catch((e) => {
        this.logger?.warn(`CallQualityMonitor: poll rejected: ${String(e)}`)
      })
    }, this.intervalMs)
  }

  /** Idempotent: clears the interval and emits the summary at most once. */
  stop(): void {
    if (this.stopped) return
    this.stopped = true
    if (this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    // Always notify the owner so it can drop its reference, even on a self-stop
    // (no Terminated) or a sample-less call. Guarded so a throwing callback
    // can't prevent the summary below or escape into a SIP state listener.
    try {
      this.onStop?.()
    } catch (e) {
      this.logger?.warn(
        `CallQualityMonitor: onStop callback threw: ${String(e)}`,
      )
    }
    // Only report calls that actually produced quality samples.
    if (this.samples.length === 0) return
    this.analytics?.track(EVENT_SUMMARY, { ...this.buildSummary() })
  }

  private async poll(): Promise<void> {
    // Skip this tick if a prior poll's getStats() is still in flight, so
    // overlapping polls can't race consecutiveFailures / prevSnapshot.
    if (this.polling) return
    this.polling = true
    try {
      let pc: RTCPeerConnection
      try {
        pc = this.getPeerConnection()
      } catch (e) {
        this.handleGetStatsFailure(e)
        return
      }

      // Media connection is terminally dead (e.g. transport lost and ICE gave
      // up). Emit the summary and stop — covers calls that never cleanly
      // reach SessionState.Terminated.
      if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
        this.logger?.info(
          `CallQualityMonitor: peer connection ${pc.connectionState}, stopping`,
        )
        this.stop()
        return
      }

      let report: RTCStatsReport
      try {
        report = await withTimeout(pc.getStats(), this.getStatsTimeoutMs)
      } catch (e) {
        this.handleGetStatsFailure(e)
        return
      }
      // stop() may have run while getStats() was in flight — don't record a
      // sample (or fire events) for a call that's already been torn down.
      if (this.stopped) return
      this.consecutiveFailures = 0
      this.processReport(report)
    } finally {
      this.polling = false
    }
  }

  private handleGetStatsFailure(e: unknown): void {
    // Connection gone or stats unavailable this tick — skip, never throw.
    this.logger?.debug('CallQualityMonitor: getStats skipped', {
      callUuid: this.callUuid,
      error: String(e),
    })
    this.consecutiveFailures += 1
    if (this.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      this.logger?.info(
        `CallQualityMonitor: stopping after ${this.consecutiveFailures} consecutive getStats failures`,
      )
      this.stop()
    }
  }

  private processReport(report: RTCStatsReport): void {
    const snapshot = parseRtcStats(report)
    const prev = this.prevSnapshot
    this.prevSnapshot = snapshot

    // Need two snapshots to compute interval rates.
    if (!prev) return

    const sample = this.deriveSample(prev, snapshot)
    // Null = nothing to measure this interval (counter reset or idle/silence);
    // the baseline has already advanced, so just wait for the next poll.
    if (!sample) return
    this.samples.push(sample)

    const isBad =
      sample.mos <= DEGRADED_MOS || sample.lossPct >= DEGRADED_LOSS_PCT
    if (isBad && !this.degraded) {
      this.degraded = true
      this.analytics?.track(EVENT_DEGRADED, {
        callUuid: this.callUuid,
        direction: this.direction,
        jitterMs: round(sample.jitterMs),
        rttMs: round(sample.rttMs),
        lossPct: round(sample.lossPct, 2),
        mos: round(sample.mos, 2),
        elapsedMs: Date.now() - this.startedAtMs,
      })
    }
  }

  private deriveSample(
    prev: RtcStatsSnapshot,
    curr: RtcStatsSnapshot,
  ): CallQualitySample | null {
    // Cumulative counters going backwards means the peer connection was
    // replaced (transfer / ICE restart); the interval can't be diffed.
    if (
      curr.packetsReceived < prev.packetsReceived ||
      curr.totalSamplesReceived < prev.totalSamplesReceived
    ) {
      return null
    }

    const deltaReceived = curr.packetsReceived - prev.packetsReceived
    const deltaLost = Math.max(0, curr.packetsLost - prev.packetsLost)
    // No RTP activity this interval (hold / silence / DTX gap) — there's no
    // audio to score, so skip rather than push a synthetic "perfect" sample.
    if (deltaReceived === 0 && deltaLost === 0) return null
    const observed = deltaReceived + deltaLost
    // Only trust a loss ratio when the interval carried enough traffic.
    const lossPct =
      observed >= MIN_INTERVAL_PACKETS ? (deltaLost / observed) * 100 : 0

    const deltaConcealed = Math.max(
      0,
      curr.concealedSamples - prev.concealedSamples,
    )
    const deltaSamples = curr.totalSamplesReceived - prev.totalSamplesReceived
    const concealmentPct =
      deltaSamples > 0 ? (deltaConcealed / deltaSamples) * 100 : 0

    const mos = estimateMos({
      rttMs: curr.rttMs,
      jitterMs: curr.jitterMs,
      lossPct,
    })

    return {
      jitterMs: curr.jitterMs,
      rttMs: curr.rttMs,
      lossPct,
      concealmentPct,
      mos,
    }
  }

  private buildSummary(): CallQualitySummary {
    const jitter = this.samples.map((s) => s.jitterMs)
    const rtt = this.samples.map((s) => s.rttMs)
    const loss = this.samples.map((s) => s.lossPct)
    const concealment = this.samples.map((s) => s.concealmentPct)
    const mos = this.samples.map((s) => s.mos)

    return {
      callUuid: this.callUuid,
      direction: this.direction,
      durationMs: Date.now() - this.startedAtMs,
      sampleCount: this.samples.length,
      avgJitterMs: round(avg(jitter)),
      maxJitterMs: round(Math.max(...jitter)),
      avgRttMs: round(avg(rtt)),
      maxRttMs: round(Math.max(...rtt)),
      avgLossPct: round(avg(loss), 2),
      maxLossPct: round(Math.max(...loss), 2),
      avgConcealmentPct: round(avg(concealment), 2),
      maxConcealmentPct: round(Math.max(...concealment), 2),
      avgMos: round(avg(mos), 2),
      minMos: round(Math.min(...mos), 2),
      floorMos: round(percentile(mos, 5), 2),
      degraded: this.degraded,
    }
  }
}

/** Reject if the wrapped promise doesn't settle within `ms`. */
const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`getStats timed out after ${ms}ms`)),
      ms,
    )
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })

const avg = (values: number[]): number =>
  values.length === 0
    ? 0
    : values.reduce((sum, v) => sum + v, 0) / values.length

/** Linear-interpolated percentile (0–100). Returns 0 for an empty input. */
const percentile = (values: number[], p: number): number => {
  if (values.length === 0) return 0
  if (values.length === 1) return values[0]
  const sorted = [...values].sort((a, b) => a - b)
  const rank = (p / 100) * (sorted.length - 1)
  const low = Math.floor(rank)
  const high = Math.ceil(rank)
  if (low === high) return sorted[low]
  const weight = rank - low
  return sorted[low] * (1 - weight) + sorted[high] * weight
}
